import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Button } from "../components/ui/button";
import { ScrollArea } from "../components/ui/scroll-area";
import CopyButton from "../components/CopyButton";
import BackButton from "../components/BackButton";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { normalizeMarkdown } from "../utils/normalizeMarkdown";

import {
  Send, Plus, History, Menu, X, Beaker, LogOut, FlaskConical, FileText,
  Database, Trash2, Loader2, MessageSquare, Brain, Paperclip,
  Shield, Sparkles, Globe, ThumbsUp, ThumbsDown, Download, Star
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const buildModes = (t) => [
  { id: "strict", label: t("chat.modeStrict"), icon: Shield, desc: t("chat.modeStrictDesc") || "Only verified KB facts" },
  { id: "hybrid", label: t("chat.modeHybrid"), icon: Sparkles, desc: t("chat.modeHybridDesc") || "KB + expert reasoning (recommended)" },
  { id: "general", label: t("chat.modeGeneral"), icon: Globe, desc: t("chat.modeGeneralDesc") || "Open polymer expertise" },
];

export default function ChatPage() {
  const { user, logout } = useAuth();
  const { t, i18n } = useTranslation();
  const MODES = buildModes(t);
  const navigate = useNavigate();
  const location = useLocation();
  const { sessionId: urlSessionId } = useParams();

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState(localStorage.getItem("chat_mode") || "hybrid");
  const [sessionId, setSessionId] = useState(urlSessionId || null);
  const [chatHistory, setChatHistory] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [attachedFiles, setAttachedFiles] = useState([]); // [{id, filename, mime}]
  const [uploadingFile, setUploadingFile] = useState(null); // {name, progress} while uploading

  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => { fetchHistory(); }, []);
  useEffect(() => { if (urlSessionId) loadSession(urlSessionId); else { setMessages([]); setSessionId(null); } }, [urlSessionId]);
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);
  useEffect(() => { localStorage.setItem("chat_mode", mode); }, [mode]);

  // Auto-grow textarea
  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = Math.min(ta.scrollHeight, 200) + "px";
  }, [input]);

  const fetchHistory = async () => {
    try {
      const res = await axios.get(`${API}/chat-history`);
      setChatHistory(res.data);
    } catch (e) { /* noop */ }
    finally { setHistoryLoading(false); }
  };

  const loadSession = async (sid) => {
    try {
      const res = await axios.get(`${API}/chat-history/${sid}`);
      setMessages(res.data.messages || []);
      setSessionId(sid);
    } catch (e) {}
  };

  const newChat = () => {
    setMessages([]);
    setSessionId(null);
    setAttachedFiles([]);
    navigate("/chat");
    setTimeout(() => textareaRef.current?.focus(), 0);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingFile({ name: file.name, progress: 0 });
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await axios.post(`${API}/chat/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (evt.total) {
            const pct = Math.round((evt.loaded * 100) / evt.total);
            setUploadingFile({ name: file.name, progress: pct });
          }
        },
      });
      setAttachedFiles((f) => [...f, res.data]);
    } catch (err) {
      alert("Upload failed: " + (err.response?.data?.detail || err.message));
    } finally {
      setUploadingFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeFile = (id) => setAttachedFiles((f) => f.filter((x) => x.id !== id));

  const send = async () => {
    if (!input.trim() || loading) return;
    const userMsg = input.trim();
    setInput("");
    const fileIds = attachedFiles.map((f) => f.id);
    const fileNote = attachedFiles.length ? `\n\n📎 Attached: ${attachedFiles.map((f) => f.filename).join(", ")}` : "";
    setMessages((m) => [...m, { role: "user", content: userMsg + fileNote, timestamp: new Date().toISOString() }]);
    setLoading(true);
    const currentFiles = [...attachedFiles];
    setAttachedFiles([]);

    try {
      const res = await axios.post(`${API}/chat`, {
        message: userMsg,
        session_id: sessionId,
        language: (i18n.language || "en").startsWith("de") ? "de" : "en",
        mode,
        file_ids: fileIds,
      });
      setMessages((m) => [...m, { role: "assistant", content: res.data.response, timestamp: new Date().toISOString(), sources: res.data.sources }]);
      if (!sessionId) {
        setSessionId(res.data.session_id);
        navigate(`/chat/${res.data.session_id}`, { replace: true });
      }
      fetchHistory();
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: "Sorry, I encountered an error. Please try again.", timestamp: new Date().toISOString() }]);
    } finally {
      setLoading(false);
    }
  };

  const deleteSession = async (sid, e) => {
    e?.stopPropagation();
    if (!window.confirm("Delete this chat session?")) return;
    try {
      await axios.delete(`${API}/chat-history/${sid}`);
      setChatHistory((h) => h.filter((s) => s.session_id !== sid));
      if (sessionId === sid) newChat();
    } catch (err) {}
  };

  const toggleSessionFav = async (sid, e) => {
    e?.stopPropagation();
    try {
      const r = await axios.patch(`${API}/chat-history/${sid}/favorite`);
      setChatHistory((h) => h.map((s) => (s.session_id === sid ? { ...s, is_favorite: r.data.is_favorite } : s)));
    } catch (err) {}
  };

  const downloadSessionPdf = async (sid, e) => {
    e?.stopPropagation();
    try {
      const r = await axios.get(`${API}/chat-history/${sid}/export-pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `elastoai_chat_${sid.slice(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) { alert("Failed to download chat PDF"); }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  const [favOnly, setFavOnly] = useState(false);

  const navItems = [
    { icon: MessageSquare, label: "Chat", path: "/chat" },
    { icon: FlaskConical, label: "Formulation", path: "/formulation" },
    { icon: FileText, label: "Documents", path: "/documents" },
    { icon: Database, label: "Knowledge", path: "/knowledge" },
    { icon: Brain, label: "Training", path: "/training" },
    ...(user?.is_admin ? [{ icon: Shield, label: "Admin", path: "/admin" }] : []),
  ];

  const submitFeedback = async (msgIdx, rating) => {
    if (!sessionId) return;
    try {
      await axios.post(`${API}/chat/feedback`, { session_id: sessionId, message_index: msgIdx, rating });
      setMessages((prev) => prev.map((m, i) => i === msgIdx ? { ...m, feedback: rating } : m));
    } catch (e) {}
  };

  return (
    <div className="flex h-screen bg-slate-50">
      <div className={`${sidebarOpen ? "w-72" : "w-0"} transition-all duration-300 bg-white border-r border-slate-200 flex flex-col overflow-hidden`}>
        <div className="p-4 border-b border-slate-200">
          <Link to="/dashboard" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-full bg-orange-500 flex items-center justify-center">
              <Beaker className="w-5 h-5 text-white" />
            </div>
            <span className="font-heading font-bold text-lg text-slate-900">ElastoAI</span>
          </Link>
        </div>

        <div className="p-4">
          <Button onClick={newChat} data-testid="new-chat-btn" className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-full">
            <Plus className="w-4 h-4 mr-2" /> New Chat
          </Button>
        </div>

        <nav className="px-3 space-y-1">
          {navItems.map((item) => (
            <Link key={item.path} to={item.path} data-testid={`nav-${item.label.toLowerCase().replace(/ /g, "-")}`}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                location.pathname.startsWith(item.path) ? "bg-slate-100 text-slate-900" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
              }`}>
              <item.icon className="w-5 h-5" /><span className="font-medium">{item.label}</span>
            </Link>
          ))}
        </nav>

        <div className="flex-1 overflow-hidden flex flex-col mt-4">
          <div className="px-4 py-2 flex items-center justify-between text-sm font-medium text-slate-500">
            <span className="flex items-center gap-2"><History className="w-4 h-4" />Recent Chats</span>
            <button onClick={() => setFavOnly((v) => !v)} data-testid="chat-fav-filter" title="Show favorites only"
              className={`p-1 rounded ${favOnly ? "bg-amber-50" : "hover:bg-slate-100"}`}>
              <Star className={`w-4 h-4 ${favOnly ? "text-amber-500 fill-amber-500" : "text-slate-400"}`} />
            </button>
          </div>
          <ScrollArea className="flex-1 px-3">
            {historyLoading ? (
              <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
            ) : chatHistory.length === 0 ? (
              <p className="text-sm text-slate-400 px-3 py-2">No chat history</p>
            ) : (favOnly ? chatHistory.filter((s) => s.is_favorite) : chatHistory).map((s) => (
              <div key={s.session_id} onClick={() => navigate(`/chat/${s.session_id}`)} data-testid={`chat-session-${s.session_id}`}
                className={`group flex items-center justify-between px-3 py-2 rounded-lg cursor-pointer mb-1 ${sessionId === s.session_id ? "bg-slate-100" : "hover:bg-slate-50"}`}>
                <span className="text-sm text-slate-700 truncate flex-1">{s.title}</span>
                <div className="flex items-center">
                  <button onClick={(e) => toggleSessionFav(s.session_id, e)}
                    className={`p-1 hover:bg-slate-200 rounded ${s.is_favorite ? "" : "opacity-0 group-hover:opacity-100"}`}
                    data-testid={`fav-session-${s.session_id}`} title={s.is_favorite ? "Remove from favorites" : "Mark as favorite"}>
                    <Star className={`w-4 h-4 ${s.is_favorite ? "text-amber-500 fill-amber-500" : "text-slate-400"}`} />
                  </button>
                  <div className="flex items-center opacity-0 group-hover:opacity-100">
                    <button onClick={(e) => downloadSessionPdf(s.session_id, e)} className="p-1 hover:bg-slate-200 rounded" data-testid={`download-session-${s.session_id}`} title="Download as PDF">
                      <Download className="w-4 h-4 text-slate-400" />
                    </button>
                    <button onClick={(e) => deleteSession(s.session_id, e)} className="p-1 hover:bg-slate-200 rounded" data-testid={`delete-session-${s.session_id}`} title="Delete">
                      <Trash2 className="w-4 h-4 text-slate-400" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </ScrollArea>
        </div>

        <div className="p-4 border-t border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-slate-200 flex items-center justify-center">
              <span className="text-sm font-medium text-slate-600">{user?.name?.charAt(0).toUpperCase()}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
              <p className="text-xs text-slate-500 truncate">{user?.email}</p>
            </div>
            <button onClick={logout} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="logout-btn">
              <LogOut className="w-4 h-4 text-slate-500" />
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 flex flex-col">
        <header className="h-16 bg-white border-b border-slate-200 flex items-center px-4 gap-3">
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="sidebar-toggle">
            {sidebarOpen ? <X className="w-5 h-5 text-slate-600" /> : <Menu className="w-5 h-5 text-slate-600" />}
          </button>
          <BackButton />
          <div className="flex-1" />
          <LanguageSwitcher className="mr-3" />
          <h1 className="font-heading font-semibold text-slate-900 mr-2">
            {sessionId ? t("chat.title") : t("chat.newChat")}
          </h1>
          {sessionId && (
            <>
              <button onClick={(e) => downloadSessionPdf(sessionId, e)} data-testid="download-current-session" className="flex items-center gap-2 px-3 py-1.5 text-sm bg-orange-50 hover:bg-orange-100 text-orange-600 rounded-lg">
                <Download className="w-4 h-4" /> Download PDF
              </button>
              <button onClick={(e) => deleteSession(sessionId, e)} data-testid="delete-current-session" className="flex items-center gap-2 px-3 py-1.5 text-sm bg-red-50 hover:bg-red-100 text-red-600 rounded-lg">
                <Trash2 className="w-4 h-4" /> Delete
              </button>
            </>
          )}
        </header>

        <div className="flex-1 overflow-y-auto p-6">
          {messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center px-4">
              <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mb-6">
                <MessageSquare className="w-8 h-8 text-slate-400" />
              </div>
              <h2 className="font-heading text-2xl font-bold text-slate-900 mb-2">Ask me anything about elastomers</h2>
              <p className="text-slate-500 max-w-md">Polymer chemistry • Vulcanization • Compounding • Processing • Testing</p>
            </div>
          ) : (
            <div className="max-w-3xl mx-auto space-y-6">
              {messages.map((msg, idx) => (
                <div key={idx} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"} group`}>
                  <div className={`px-5 py-4 rounded-2xl ${msg.role === "user" ? "bg-slate-900 text-white max-w-[85%]" : "bg-white border border-slate-200 text-slate-900 shadow-sm max-w-[95%]"}`}
                       data-testid={`message-${msg.role}-${idx}`}>
                    {msg.role === "assistant" ? (
                      <>
                        <div className="prose prose-sm max-w-none break-words prose-p:my-4 prose-p:leading-7 prose-headings:mt-6 prose-headings:mb-3 prose-h1:text-lg prose-h2:text-base prose-h2:font-semibold prose-h3:text-sm prose-h3:font-semibold prose-ul:my-3 prose-ul:pl-5 prose-ol:my-3 prose-ol:pl-5 prose-li:my-1.5 prose-li:marker:text-slate-400 prose-strong:text-slate-900 prose-strong:font-semibold prose-code:bg-slate-100 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-slate-800 prose-code:before:content-none prose-code:after:content-none">
                          <ReactMarkdown
                            remarkPlugins={[remarkGfm]}
                            components={{
                              img: (p) => <img {...p} alt={p.alt || ""} style={{ maxWidth: "100%", borderRadius: 10, marginTop: 12, marginBottom: 12 }} />,
                              table: (p) => (
                                <div className="overflow-x-auto my-3 -mx-2 px-2">
                                  <table className="w-full border-collapse text-sm" {...p} />
                                </div>
                              ),
                              th: (p) => <th className="border border-slate-200 px-3 py-1.5 bg-slate-50 text-left font-semibold" {...p} />,
                              td: (p) => <td className="border border-slate-200 px-3 py-1.5 align-top" {...p} />,
                            }}
                          >{normalizeMarkdown(msg.content)}</ReactMarkdown>
                        </div>
                        {msg.sources?.length > 0 && (
                          <div className="mt-3 pt-2 border-t border-slate-100 flex flex-wrap gap-1">
                            {msg.sources.map((s, i) => (
                              <span key={i} className="text-xs px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full">{s}</span>
                            ))}
                          </div>
                        )}
                        <div className="mt-2 flex items-center gap-1">
                          <CopyButton text={msg.content} label />
                          {sessionId && (
                            <>
                              <button onClick={() => submitFeedback(idx, "up")} data-testid={`feedback-up-${idx}`}
                                className={`inline-flex items-center gap-1 text-xs rounded px-2 py-1 transition-colors ${msg.feedback === "up" ? "text-green-600 bg-green-50" : "text-slate-400 hover:text-green-600 hover:bg-slate-100"}`}
                                title="Helpful">
                                <ThumbsUp className="w-3.5 h-3.5" />
                              </button>
                              <button onClick={() => submitFeedback(idx, "down")} data-testid={`feedback-down-${idx}`}
                                className={`inline-flex items-center gap-1 text-xs rounded px-2 py-1 transition-colors ${msg.feedback === "down" ? "text-red-600 bg-red-50" : "text-slate-400 hover:text-red-600 hover:bg-slate-100"}`}
                                title="Not helpful">
                                <ThumbsDown className="w-3.5 h-3.5" />
                              </button>
                            </>
                          )}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="whitespace-pre-wrap break-words">{msg.content}</div>
                        <div className="mt-1 flex justify-end opacity-0 group-hover:opacity-100 transition-opacity">
                          <CopyButton text={msg.content} className="text-slate-300 hover:text-white hover:bg-slate-800" />
                        </div>
                      </>
                    )}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-white border border-slate-200 px-4 py-3 rounded-2xl flex items-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                    <span className="text-sm text-slate-400">Thinking...</span>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Mode selector + input */}
        <div className="border-t border-slate-200 bg-white">
          <div className="max-w-3xl mx-auto px-4 py-3">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              {MODES.map((m) => (
                <button key={m.id} onClick={() => setMode(m.id)} data-testid={`mode-${m.id}`}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                    mode === m.id ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`} title={m.desc}>
                  <m.icon className="w-3.5 h-3.5" />{m.label}
                </button>
              ))}
              <span className="text-xs text-slate-400 ml-2 hidden md:inline">{MODES.find((x) => x.id === mode)?.desc}</span>
            </div>

            {uploadingFile && (
              <div className="flex items-center gap-3 mb-2 p-3 bg-blue-50 border border-blue-200 rounded-xl" data-testid="chat-upload-progress">
                <Loader2 className="w-4 h-4 animate-spin text-blue-600 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-blue-900 truncate">Uploading {uploadingFile.name}…</p>
                  <div className="h-1.5 bg-blue-100 rounded-full overflow-hidden mt-1">
                    <div className="h-full bg-blue-500 rounded-full transition-all duration-200" style={{ width: `${uploadingFile.progress}%` }} />
                  </div>
                </div>
                <span className="text-xs text-blue-700 font-mono flex-shrink-0">{uploadingFile.progress}%</span>
              </div>
            )}

            {attachedFiles.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {attachedFiles.map((f) => (
                  <div key={f.id} className="flex items-center gap-2 px-3 py-1.5 bg-orange-50 border border-orange-200 rounded-full text-xs text-orange-700" data-testid={`attached-file-${f.id}`}>
                    <Paperclip className="w-3 h-3" />{f.filename}
                    <button onClick={() => removeFile(f.id)} className="hover:bg-orange-100 rounded-full p-0.5">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="flex items-end gap-3">
              <input ref={fileInputRef} type="file" accept=".pdf,.xlsx,.xls,.csv,.txt,.png,.jpg,.jpeg,.webp" className="hidden" onChange={handleFileUpload} data-testid="chat-file-input" />
              <button onClick={() => fileInputRef.current?.click()} data-testid="chat-attach-btn" disabled={!!uploadingFile}
                className="h-12 w-12 flex items-center justify-center bg-slate-100 hover:bg-slate-200 disabled:opacity-50 rounded-full text-slate-600">
                {uploadingFile ? <Loader2 className="w-5 h-5 animate-spin" /> : <Paperclip className="w-5 h-5" />}
              </button>
              <textarea
                ref={textareaRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKey}
                rows={1}
                placeholder={t("chat.placeholder")}
                data-testid="chat-input"
                disabled={loading}
                className="flex-1 resize-none min-h-[48px] max-h-[200px] px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-slate-900 text-sm"
              />
              <Button onClick={send} disabled={!input.trim() || loading} data-testid="send-message-btn"
                className="h-12 w-12 bg-slate-900 hover:bg-slate-800 text-white rounded-full p-0 flex-shrink-0">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
              </Button>
            </div>
            <p className="text-[11px] text-slate-400 mt-2 text-center">ElastoAI is polymer/elastomer-only. Verify formulations experimentally before production.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
